import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/authAPI';
import type { User } from '../../services/authAPI';
import JRLogoWithText from './JRLogoWithText';
import { restartOnboarding } from '../onboarding/OnboardingTour';
import { useAlerts } from '../../hooks/useAlerts';
import NotificationList from '../common/NotificationList';

interface HeaderProps {
  selectedMarket: string;
  onMarketChange: (market: string) => void;
}

export default function Header({ selectedMarket, onMarketChange }: HeaderProps) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);

  const markets = [
    { value: 'US', label: '美股', icon: 'ri-flag-line' },
    { value: 'CN', label: 'A股', icon: 'ri-flag-line' },
    { value: 'HK', label: '港股', icon: 'ri-flag-line' }
  ];

  // 通知/告警(挂载 + 60s 轮询)抽到 useAlerts,桌面铃铛与移动端「通知」Tab 共用一份
  const { notifications, unreadCount, markRead, markAllRead } = useAlerts();

  // 获取用户信息
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // 首先尝试从 localStorage 读取
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
          try {
            const user = JSON.parse(storedUserInfo);
            setUserInfo(user);
            setLoadingUserInfo(false);
          } catch (e) {
            console.error('解析用户信息失败:', e);
          }
        }

        // 然后从 API 获取最新信息
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const response = await authAPI.getCurrentUser();
            if (response.success && response.data?.user) {
              setUserInfo(response.data.user);
              localStorage.setItem('userInfo', JSON.stringify(response.data.user));
            }
          } catch (error) {
            console.error('获取用户信息失败:', error);
          }
        }
      } catch (error) {
        console.error('获取用户信息异常:', error);
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  // 退出登录
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 无论 API 是否成功，都清除本地存储并跳转
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      navigate('/auth/login');
    }
  };

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (userInfo) {
      return userInfo.username || userInfo.email || '用户';
    }
    return '用户';
  };

  // 获取用户角色显示
  const getUserRoleDisplay = () => {
    // 可以根据实际需求映射角色
    return '用户';
  };

  const userMenuItems = [
    { icon: 'ri-user-line', label: '个人资料', action: () => console.log('个人资料'), color: 'blue' },
    { icon: 'ri-wallet-line', label: '账户余额', action: () => console.log('账户余额'), color: 'green' },
    { icon: 'ri-history-line', label: '使用记录', action: () => console.log('使用记录'), color: 'purple' },
    { icon: 'ri-guide-line', label: '重看引导', action: restartOnboarding, color: 'teal' },
    { icon: 'ri-logout-box-line', label: '退出登录', action: handleLogout, color: 'red' },
  ];

  const settingsMenuItems = [
    { icon: 'ri-palette-line', label: '主题设置', action: () => console.log('主题设置'), color: 'pink' },
    { icon: 'ri-notification-line', label: '通知设置', action: () => console.log('通知设置'), color: 'orange' },
    { icon: 'ri-shield-check-line', label: '安全设置', action: () => console.log('安全设置'), color: 'teal' },
    { icon: 'ri-question-line', label: '帮助中心', action: () => navigate('/app/help'), color: 'indigo' },
  ];

  return (
    <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-40">
      <div className="px-6 py-3">
        <div className="flex items-center justify-between">
          {/* 左侧：标题和市场切换 */}
          <div className="flex items-center">
            {/* Logo - 使用首页的JRLogoWithText组件 */}
            <div className="flex items-center">
              <JRLogoWithText logoSize={42} color="#1e40af" />
            </div>
            
            {/* 市场切换 - 左侧与面包屑左侧对齐 */}
            <div className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl px-4 py-2.5 border border-gray-200 ml-16">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white shadow-sm">
                <i className="ri-global-line text-teal-600"></i>
              </div>
              <span className="text-sm font-medium text-gray-600">市场:</span>
              <select 
                value={selectedMarket}
                onChange={(e) => onMarketChange(e.target.value)}
                className="bg-transparent border-none text-sm font-semibold text-gray-900 focus:outline-none cursor-pointer pr-6"
              >
                {markets.map(market => (
                  <option key={market.value} value={market.value}>
                    {market.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* 右侧：通知、设置、用户 */}
          <div className="flex items-center space-x-3">
            {/* 通知按钮 */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowSettingsMenu(false);
                  setShowUserMenu(false);
                }}
                className="relative w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 transition-all duration-300 cursor-pointer group shadow-sm hover:shadow-md"
              >
                <i className="ri-notification-3-line text-xl text-gray-600 group-hover:text-gray-900 transition-colors"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-red-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowNotifications(false)}
                  ></div>
                  <div className="absolute right-0 mt-3 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-20 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-gray-900">通知中心</h3>
                        {unreadCount > 0 && (
                          <span className="px-2.5 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-semibold rounded-full">
                            {unreadCount} 条未读
                          </span>
                        )}
                      </div>
                    </div>
                    <NotificationList
                      notifications={notifications}
                      unreadCount={unreadCount}
                      markRead={markRead}
                      markAllRead={markAllRead}
                    />
                  </div>
                </>
              )}
            </div>

            {/* 设置菜单 */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSettingsMenu(!showSettingsMenu);
                  setShowUserMenu(false);
                  setShowNotifications(false);
                }}
                className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 transition-all duration-300 cursor-pointer group shadow-sm hover:shadow-md"
              >
                <i className={`ri-settings-3-line text-xl text-gray-600 group-hover:text-gray-900 transition-all duration-300 ${showSettingsMenu ? 'rotate-90' : ''}`}></i>
              </button>

              {showSettingsMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowSettingsMenu(false)}
                  ></div>
                  <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 z-20 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-bold text-gray-900">系统设置</h3>
                    </div>
                    {settingsMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          item.action();
                          setShowSettingsMenu(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all cursor-pointer group"
                      >
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-${item.color}-50 group-hover:bg-${item.color}-100 transition-colors shadow-sm`}>
                          <i className={`${item.icon} text-${item.color}-600 text-lg`}></i>
                        </div>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* 用户菜单 */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowSettingsMenu(false);
                  setShowNotifications(false);
                }}
                className="flex items-center space-x-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 rounded-xl px-3 py-2 transition-all duration-300 cursor-pointer group border border-transparent hover:border-gray-200"
              >
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-400 via-emerald-500 to-cyan-500 flex items-center justify-center text-white font-semibold shadow-lg group-hover:shadow-xl transition-shadow overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20with%20clean%20background%2C%20modern%20style%2C%20friendly%20expression%2C%20high%20quality%20headshot&width=100&height=100&seq=user-avatar-header-001&orientation=squarish" 
                      alt="User Avatar" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full"></div>
                </div>
                <div className="hidden lg:block text-left">
                  <div className="text-sm font-semibold text-gray-900">
                    {loadingUserInfo ? '加载中...' : getUserDisplayName()}
                  </div>
                  <div className="text-xs text-gray-500">{getUserRoleDisplay()}</div>
                </div>
                <i className={`ri-arrow-down-s-line text-gray-400 group-hover:text-gray-600 transition-all duration-300 ${showUserMenu ? 'rotate-180' : ''}`}></i>
              </button>

              {showUserMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowUserMenu(false)}
                  ></div>
                  <div className="absolute right-0 mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 z-20 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-emerald-50">
                      <div className="flex items-center space-x-3">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-400 via-emerald-500 to-cyan-500 flex items-center justify-center text-white font-semibold shadow-lg overflow-hidden">
                          <img 
                            src="https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20with%20clean%20background%2C%20modern%20style%2C%20friendly%20expression%2C%20high%20quality%20headshot&width=100&height=100&seq=user-avatar-header-001&orientation=squarish" 
                            alt="User Avatar" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <div className="text-base font-bold text-gray-900">
                            {loadingUserInfo ? '加载中...' : getUserDisplayName()}
                          </div>
                          <div className="text-sm text-gray-600">
                            {userInfo?.email || ''}
                          </div>
                          <div className="mt-1 inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-semibold rounded-full">
                            <i className="ri-vip-crown-line mr-1"></i>
                            {getUserRoleDisplay()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="py-2">
                      {userMenuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            item.action();
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center space-x-3 px-5 py-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all cursor-pointer group"
                        >
                          <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-${item.color}-50 group-hover:bg-${item.color}-100 transition-colors shadow-sm`}>
                            <i className={`${item.icon} text-${item.color}-600 text-lg`}></i>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
